package com.upcominds.expedite.data

import com.upcominds.expedite.data.auth.AuthRepository
import com.upcominds.expedite.data.auth.SessionExpiredException
import io.ktor.client.HttpClient
import io.ktor.client.HttpClientConfig
import io.ktor.client.plugins.auth.Auth
import io.ktor.client.plugins.plugin
import io.ktor.client.plugins.auth.providers.BearerAuthProvider
import io.ktor.client.plugins.auth.providers.BearerTokens
import io.ktor.client.plugins.auth.providers.bearer
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.HttpResponseValidator
import io.ktor.client.statement.bodyAsText
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json
import com.upcominds.expedite.di.getKoin
import io.ktor.http.isSuccess

// Shared HttpClient for repositories to reuse
object NetworkClient {
    // Centralized base URL for the API. You can change this at runtime via property assignment:
    // e.g. NetworkClient.baseUrl = "https://api.example.com"
    var baseUrl: String = AppConfig.baseUrl

    private val authRepository: AuthRepository = getKoin().get()

    init {
        println("NetworkClient: Initializing")
    }

    private fun HttpClientConfig<*>.installJsonSupport() {
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                useAlternativeNames = false
            })
        }
    }

    private fun HttpClientConfig<*>.installFriendlyHttpErrors() {
        HttpResponseValidator {
            validateResponse { response ->
                if (response.status.isSuccess()) return@validateResponse

                val bodyText = runCatching { response.bodyAsText() }.getOrDefault("")
                val message = mapApiError(
                    statusCode = response.status.value,
                    bodyText = bodyText,
                    requestUrl = response.call.request.url.toString()
                )

                throw ApiException(
                    statusCode = response.status.value,
                    message = message,
                    responseBody = bodyText
                )
            }
        }
    }

    val httpClient: HttpClient by lazy {
        println("NetworkClient: Creating HttpClient")
        HttpClient {
            installJsonSupport()
            installFriendlyHttpErrors()

            install(Auth) {
                bearer {
                    // Always load the latest tokens from storage
                    sendWithoutRequest { true }
                    loadTokens {
                        println("NetworkClient: loadTokens called")
                        val tokens = if (authRepository.getTokenStorage().load() == null) null else {
                            val access = authRepository.getValidAccessToken()
                            BearerTokens(access, authRepository.getTokenStorage().load()?.refreshToken ?: "")
                        }
                        println("NetworkClient: loadTokens returning tokens: ${tokens != null}")
                        tokens
                    }
                    refreshTokens {
                        println("NetworkClient: refreshTokens called")
                        if (authRepository.getTokenStorage().load() == null) {
                            return@refreshTokens null
                        }

                        try {
                            val newTokens = authRepository.refreshToken(
                                failedAccessToken = oldTokens?.accessToken
                            )
                            BearerTokens(newTokens.accessToken, newTokens.refreshToken ?: "")
                        } catch (_: SessionExpiredException) {
                            null
                        }
                    }
                }
            }
        }
    }

    val publicHttpClient: HttpClient by lazy {
        println("NetworkClient: Creating public HttpClient")
        HttpClient {
            installJsonSupport()
            installFriendlyHttpErrors()
        }
    }

    fun clearAuthTokenCache() {
        httpClient.plugin(Auth).providers
            .filterIsInstance<BearerAuthProvider>()
            .forEach { it.clearToken() }
    }

    // Build a full endpoint URL by concatenating baseUrl and the provided path safely.
    fun endpoint(path: String): String = baseUrl.trimEnd('/') + "/" + path.trimStart('/')
}
